import React, { useCallback, useEffect, useRef } from 'react';

import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap, cx } from '@atlaskit/css';
import { useFlags } from '@atlaskit/flag/use-flags';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import type { GenerateImageProps } from '@atlassian/ai-image-generation/types';

import { useCompanyHubImageGeneration } from '../../../controllers/use-company-hub-image-generation';
import { ITEM_FIELDS } from '../../../types';
import type { UpdateItemFormFieldType } from '../../items-shared/types';
import { MediaPickerSection, type SelectedImage, type UploadedImage } from '../../media-components';
import { messages } from './messages';

const styles = cssMap({
	fieldWrapper: {
		marginTop: token('space.200'),
	},
});

type ImageFieldProps = {
	analyticsSource: string;
	contentId: string;
	imageAltText?: string;
	imageSrc?: string;
	itemId: string;
	shouldAllowLinkFromUrl?: boolean;
	updateItemFormField: UpdateItemFormFieldType;
};

export const ImageField = ({
	analyticsSource,
	itemId,
	contentId,
	imageSrc,
	imageAltText,
	updateItemFormField,
	shouldAllowLinkFromUrl = false,
}: ImageFieldProps): React.JSX.Element => {
	// Gate the Premium Extensions image generation experiment behind the
	// `confluence-ai` feature so that AI-related UI is suppressed for IC
	// (Isolated Cloud) tenants where Confluence AI is not available.
	const isAiImgGenEnabled =
		isExperimentEnabled('cc_page_experiences_premium_macros_img_generation') &&
		isFeatureEnabled('confluence', 'confluence-ai');

	const {
		generateImage,
		aiHeaderImageStatus,
		aiHeaderImageFileId,
		error: aiHeaderImageError,
	} = useCompanyHubImageGeneration({
		contentId,
	});

	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isAiHeaderImageLoading =
		(aiHeaderImageStatus === 'LOADING' || aiHeaderImageStatus === 'LOADING_WITH_ACCEPTANCE_FLOW') &&
		!aiHeaderImageFileId;

	// Fire an analytics event when an AI-generated header image has been
	// produced successfully. We fire once per generation by tracking when
	// `aiHeaderImageFileId` transitions from falsy to truthy. Mirrors the
	// `headerImageAdded` analytics fired by Confluence's
	// EditorPageCoverPicture.handleAIGeneratedUpload, but adapted for the
	// company hub config panel.
	const prevAIHeaderImageFileIdRef = useRef<string | undefined | null>(null);
	useEffect(() => {
		if (!isAiImgGenEnabled) {
			prevAIHeaderImageFileIdRef.current = aiHeaderImageFileId;
			return;
		}

		const prevFileId = prevAIHeaderImageFileIdRef.current;
		const isNewFileId = !prevFileId && !!aiHeaderImageFileId;
		prevAIHeaderImageFileIdRef.current = aiHeaderImageFileId;

		if (!isNewFileId) {
			return;
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'added',
				actionSubject: 'image',
				actionSubjectId: 'companyHubImageAdded',
				source: analyticsSource,
				attributes: {
					imageSource: 'aiGenerated',
					imageId: aiHeaderImageFileId,
					inputMethod: 'configPanel',
					aiHeaderImageStatus,
					isAIImgGenEnabled: isAiImgGenEnabled,
				},
			},
		}).fire();
	}, [
		aiHeaderImageFileId,
		aiHeaderImageStatus,
		isAiImgGenEnabled,
		createAnalyticsEvent,
		analyticsSource,
	]);

	// Surface a flag to the user when AI image generation fails. We only fire
	// when the status transitions INTO `ERROR` (mirroring the Confluence header
	// image behaviour in EditorPageCoverPicture) so re-renders while already in
	// the error state don't produce duplicate flags.
	//
	// Each fire uses a unique flag `id` so that consecutive failures STACK in
	// the flag tray (matching the Confluence header image behaviour) rather
	// than replacing the previous flag — ADS `useFlags` dedupes by `id`.
	const prevAIHeaderImageStatusRef = useRef<typeof aiHeaderImageStatus | null>(null);
	const aiGenerationErrorFlagCounterRef = useRef(0);
	useEffect(() => {
		if (!isAiImgGenEnabled) {
			prevAIHeaderImageStatusRef.current = aiHeaderImageStatus;
			return;
		}

		const prevStatus = prevAIHeaderImageStatusRef.current;
		const isTransitioningToError = prevStatus !== 'ERROR' && aiHeaderImageStatus === 'ERROR';
		prevAIHeaderImageStatusRef.current = aiHeaderImageStatus;

		if (!isTransitioningToError) {
			return;
		}

		const description = aiHeaderImageError
			? formatMessage(messages.aiGenerationErrorFlagDescriptionWithReason, {
					errorReason: aiHeaderImageError,
				})
			: formatMessage(messages.aiGenerationErrorFlagDescription);

		aiGenerationErrorFlagCounterRef.current += 1;
		void showFlag({
			id: `company-hub-image-field-ai-generation-error-flag-${aiGenerationErrorFlagCounterRef.current}`,
			title: formatMessage(messages.aiGenerationErrorFlagTitle),
			description,
			icon: <StatusErrorIcon label="" size="medium" color={token('color.icon.danger')} />,
		});
	}, [aiHeaderImageStatus, aiHeaderImageError, isAiImgGenEnabled, showFlag, formatMessage]);

	const generateImageRef = useRef(generateImage);
	generateImageRef.current = generateImage;

	const generateAndApplyImage = useCallback(
		async (props: GenerateImageProps) => {
			const mediaFileId = await generateImageRef.current(props);
			if (mediaFileId) {
				updateItemFormField(itemId, {
					[ITEM_FIELDS.IMAGE_SRC]: mediaFileId,
					[ITEM_FIELDS.IMAGE_ALT_TEXT]: '',
				});
			}
			return mediaFileId;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[itemId, updateItemFormField],
	);

	const handleChangeImage = useCallback(
		(props: SelectedImage | UploadedImage) => {
			const newImageSrc =
				'fullSizeImageUrl' in props ? props.fullSizeImageUrl : props.uploadedImageId;
			const newAltText = props.altDescription;

			updateItemFormField(itemId, {
				[ITEM_FIELDS.IMAGE_SRC]: newImageSrc,
				[ITEM_FIELDS.IMAGE_ALT_TEXT]: newAltText,
			});
		},
		[itemId, updateItemFormField],
	);

	return (
		<Flex xcss={cx(styles.fieldWrapper)} direction="column">
			<Inline spread="space-between" alignBlock="center" testId="image-field">
				<Text>
					<FormattedMessage {...messages.imageLabel} />
				</Text>
				<MediaPickerSection
					shouldAllowLinkFromUrl={shouldAllowLinkFromUrl}
					contentId={contentId}
					image={imageSrc ? { imageSrc, altText: imageAltText } : undefined}
					onChangeImage={handleChangeImage}
					analyticsSource={analyticsSource}
					shouldShowDeleteButton
					generateAIHeaderImage={isAiImgGenEnabled ? generateAndApplyImage : undefined}
					isAiHeaderImageLoading={isAiImgGenEnabled ? isAiHeaderImageLoading : false}
				/>
			</Inline>
		</Flex>
	);
};
